import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type { FC } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { WebHookDeliveries } from './WebHookDeliveries';
import { WebHookDetail } from './WebHookDetail';
import { WebHooksList } from './WebHooksList';
import { useWebHooks } from './useWebHooks';
import './WebHooks.css';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { CopyTextDialog } from 'components/CopyTextDialog/CopyTextDialog';
import { useLocale } from 'containers/App/LocaleContext';
import { WEBHOOKS_BASE_URL, WEBHOOKS_DELIVERIES_URL } from './constants';

export const WebHooks: FC = () => {
  const {
    dataFetching,
    commands: {
      loadCACerts,
      loadWebHooks,
      saveWebHook,
      testWebHook,
      closeSavedSecretDialog,
    },
    eventTypes,
    caCerts,
    webhooks,
    savedSecret,
    detailLoadingState,
  } = useWebHooks();
  const msg = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const onDeliveriesTab = location.pathname.startsWith(WEBHOOKS_DELIVERIES_URL);
  const caCertOptions = caCerts.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  if (dataFetching) {
    return (
      <div className='flex flex-1 flex-col justify-center'>
        <LoaderAndText />
      </div>
    );
  }

  return (
    <>
      <CopyTextDialog
        text={savedSecret}
        onClose={closeSavedSecretDialog}
        msg={msg.settings.webHooks.savedSecretDialog}
      />
      <PageHeader width='full' label={msg.settings.webHooks.heading} />
      <PageLayout className='flex flex-col'>
        <p>{msg.settings.webHooks.help}</p>
        <Tabs
          variant='inner'
          value={onDeliveriesTab ? 'deliveries' : 'list'}
          onChange={(tab) =>
            navigate(
              tab === 'deliveries'
                ? WEBHOOKS_DELIVERIES_URL
                : WEBHOOKS_BASE_URL,
            )
          }
          tabs={[
            { id: 'list', label: msg.settings.webHooks.heading },
            {
              id: 'deliveries',
              label: msg.settings.webHooks.deliveries.recentDeliveries,
            },
          ]}
        />
        {onDeliveriesTab ? (
          <WebHookDeliveries />
        ) : (
          <>
            {/* The list is always mounted; the create/edit routes render the
                editor SidePanel as an overlay on top of it. */}
            <WebHooksList
              msg={msg}
              eventTypes={eventTypes}
              reloadWebHooks={() => loadWebHooks({ background: true })}
              testWebHook={testWebHook}
              webhooks={webhooks}
            />
            <Routes>
              <Route
                path='create'
                element={
                  <WebHookDetail
                    caCertOptions={caCertOptions}
                    eventTypes={eventTypes}
                    reloadCACerts={loadCACerts}
                    saveWebHook={saveWebHook}
                    type='create'
                    webhooks={[]}
                    detailLoadingState={detailLoadingState}
                  />
                }
              />
              <Route
                path=':webhookId'
                element={
                  <WebHookDetail
                    caCertOptions={caCertOptions}
                    eventTypes={eventTypes}
                    reloadCACerts={loadCACerts}
                    saveWebHook={saveWebHook}
                    type='edit'
                    webhooks={webhooks}
                    detailLoadingState={detailLoadingState}
                  />
                }
              />
            </Routes>
          </>
        )}
      </PageLayout>
    </>
  );
};
